import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { motion, Variants, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";

const parseWord = (raw: string, key: number): React.ReactNode => {
  const boldMatch = raw.match(/^\*\*(.+?)\*\*(.*)$/);
  if (boldMatch) {
    return (
      <React.Fragment key={key}>
        <strong style={{ fontWeight: 600 }}>{boldMatch[1]}</strong>
        {boldMatch[2]}
      </React.Fragment>
    );
  }
  const italicMatch = raw.match(/^_(.+?)_(.*)$/);
  if (italicMatch) {
    return (
      <React.Fragment key={key}>
        <em style={{ fontStyle: "italic" }}>{italicMatch[1]}</em>
        {italicMatch[2]}
      </React.Fragment>
    );
  }
  return raw;
};

const Word = ({ children, progress, range }: any) => {
  const opacity = useTransform(progress, range, [0.15, 1]);
  return (
    <motion.span style={{ opacity, display: "inline-block" }}>
      {children}
    </motion.span>
  );
};

const BLOCK: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.13 } },
};

const ITEM: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.4, ease: [0.16, 0.77, 0.47, 0.97] },
  },
};

const viewport = { once: true, amount: 0.1 };

const WhatIsKanonics: React.FC = () => {
  const { t } = useTranslation();
  const label = t("whatis.label");
  return (
    <Box
      component="section"
      id="about"
      sx={{
        bgcolor: "#f8f9fb",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: 0,
          left: 0,
          width: "calc(max(0px, (100% - 1280px) / 2) + 258px)",
          height: "1px",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: 0,
          left: "calc(max(0px, (100% - 1280px) / 2) + 358px)",
          right: 0,
          height: "1px",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />

      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: "-25vh",
          left: "calc(max(0px, (100% - 1280px) / 2) + 308px)",
          width: "1px",
          height: "calc(25vh + 100%)",
          bgcolor: "rgba(29, 47, 65, 0.15)",
        }}
      />

      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          px: { xs: 3, md: 6 },
          py: { xs: 8, md: 12 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 6, md: 0 },
        }}
      >
        {/* Left label */}
        <Box
          sx={{
            flexShrink: 0,
            width: { md: "260px" },
            pr: { md: 6 },
            bgcolor: "#EBEDF0",
          }}
        >
          <Box sx={{ position: { md: "sticky" }, top: { md: "120px" } }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={BLOCK}
            >
              <motion.div variants={ITEM} style={{ willChange: "transform" }}>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "#1D2F41",
                    fontFamily: '"Calibri", sans-serif',
                    lineHeight: 1.5,
                    pt: "10px",
                    pl: "10px",
                    pb: "10px",
                  }}
                >
                  {label.split("\n").map((line: string, i: number) => (
                    <React.Fragment key={i}>
                      {i > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </Typography>
              </motion.div>
            </motion.div>
          </Box>
        </Box>

        {/* Right body */}
        <Box sx={{ flex: 1, pl: { md: 8 } }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={BLOCK}
          >
            <motion.div variants={ITEM} style={{ willChange: "transform" }}>
              <ScrollRevealText />
            </motion.div>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
};

const ScrollRevealText = () => {
  const { t } = useTranslation();
  const rawWords = t("whatis.words", { returnObjects: true }) as string[];
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 85%", "end 55%"],
  });

  return (
    <Typography
      ref={containerRef}
      sx={{
        fontSize: { xs: "1.35rem", md: "1.65rem", lg: "1.85rem" },
        lineHeight: 1.5,
        color: "#1D2F41",
        fontFamily: '"Calibri", sans-serif',
        fontWeight: 300,
        position: "relative",
      }}
    >
      {rawWords.map((raw, i) => {
        const start = i / rawWords.length;
        const end = start + 1 / rawWords.length;
        return (
          <React.Fragment key={i}>
            <Word progress={scrollYProgress} range={[start, end]}>
              {parseWord(raw, i)}
            </Word>{" "}
          </React.Fragment>
        );
      })}
    </Typography>
  );
};

export default WhatIsKanonics;
